import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const Ctx = createContext(null);

export function UnsavedChangesProvider({ children }) {
  const [dirtyMap, setDirtyMap] = useState({});
  const dirty = Object.values(dirtyMap).some(Boolean);

  const setDirty = useCallback((id, isDirty) => {
    setDirtyMap((m) => {
      if (!!m[id] === !!isDirty) return m;
      const next = { ...m };
      if (isDirty) next[id] = true; else delete next[id];
      return next;
    });
  }, []);

  // Browser-level guard (reload / tab-close / URL change) while anything is dirty.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const value = useMemo(() => ({ dirty, setDirty }), [dirty, setDirty]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUnsavedChangesContext() {
  return useContext(Ctx);
}

// Editors call this to report whether they have unsaved edits.
export function useUnsavedChanges(id, isDirty) {
  const ctx = useContext(Ctx);
  const setDirty = ctx?.setDirty;
  useEffect(() => {
    setDirty?.(id, isDirty);
  }, [setDirty, id, isDirty]);
  useEffect(() => {
    return () => setDirty?.(id, false);
  }, [setDirty, id]);
}
